import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './SubscriptionDetails.module.scss';
import { useUserState } from '../../../providers/user-state.provider.ts';
import { getSubscriptionDetails } from '../../../services/payment.service';
import { Subscription } from '../../../types/payment.types';
import AccountTabs from '../../Account/AccountTabs';
import BillingPortalPanel from '../../Account/BillingPortalPanel';
import {
    isSubscriptionCheckoutRecoverable,
    isSubscriptionNonTerminal,
    subscriptionGrantsAccess
} from '../../../utils/subscription';
import { getPaymentErrorMessage } from '../../../utils/payment-error';
import { usePageActivationRefresh } from '../../../hooks/usePageActivationRefresh';
import { buildJoinUrl } from '../../../utils/join';
import { Routes } from '../../../types/constants';

const SubscriptionDetails = () => {
    const [subscription, setSubscription] = useState<Subscription | null>(null);
    const [error, setError] = useState<{ message: string; status: number } | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const { getUserInfo } = useUserState();
    const token = getUserInfo()?.token ?? '';
    const [subscriptionAuthKey, setSubscriptionAuthKey] = useState(token);
    const authKeyRef = useRef(token);
    const refreshPromiseRef = useRef<{ authKey: string; promise: Promise<void> } | null>(null);
    const trailingRefreshPromiseRef = useRef<{ authKey: string; promise: Promise<void> } | null>(null);
    // Invalidate old-account requests before effects run so they cannot populate the new account view.
    if (authKeyRef.current !== token) {
        authKeyRef.current = token;
        refreshPromiseRef.current = null;
        trailingRefreshPromiseRef.current = null;
    }

    const handleError = useCallback((requestedAuthKey: string, err: any) => {
        if (authKeyRef.current !== requestedAuthKey) {
            return;
        }

        setSubscriptionAuthKey(requestedAuthKey);
        setSubscription(null);
        setError({
            message: getPaymentErrorMessage(err),
            status: err?.response?.status || 500
        });
    }, []);

    const startSubscriptionDetailsRefresh = useCallback((requestedAuthKey: string): Promise<void> => {
        const refreshPromise = (async () => {
            if (!requestedAuthKey) {
                setSubscriptionAuthKey(requestedAuthKey);
                setSubscription(null);
                setError({ message: 'User is not authenticated. Please log in.', status: 401 });
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            try {
                const response = await getSubscriptionDetails(requestedAuthKey);
                if (authKeyRef.current !== requestedAuthKey) {
                    return;
                }

                setSubscriptionAuthKey(requestedAuthKey);
                setSubscription(response);
                setError(null);
            } catch (err: any) {
                handleError(requestedAuthKey, err);
            } finally {
                if (authKeyRef.current === requestedAuthKey) {
                    setIsLoading(false);
                }
            }
        })();

        refreshPromiseRef.current = { authKey: requestedAuthKey, promise: refreshPromise };
        void refreshPromise.finally(() => {
            if (refreshPromiseRef.current?.promise === refreshPromise) {
                refreshPromiseRef.current = null;
            }
        });
        return refreshPromise;
    }, [handleError]);

    const refreshSubscriptionDetails = useCallback((): Promise<void> => {
        const inFlightRefresh = refreshPromiseRef.current;
        if (inFlightRefresh?.authKey !== token) {
            return startSubscriptionDetailsRefresh(token);
        }

        if (trailingRefreshPromiseRef.current?.authKey === token) {
            return trailingRefreshPromiseRef.current.promise;
        }

        const requestedAuthKey = token;
        const trailingPromise = inFlightRefresh.promise
            .catch(() => undefined)
            .then(() => {
                if (trailingRefreshPromiseRef.current?.promise === trailingPromise) {
                    trailingRefreshPromiseRef.current = null;
                }
                if (authKeyRef.current === requestedAuthKey) {
                    return startSubscriptionDetailsRefresh(requestedAuthKey);
                }
                return undefined;
            });
        trailingRefreshPromiseRef.current = { authKey: requestedAuthKey, promise: trailingPromise };
        void trailingPromise.finally(() => {
            if (trailingRefreshPromiseRef.current?.promise === trailingPromise) {
                trailingRefreshPromiseRef.current = null;
            }
        });
        return trailingPromise;
    }, [startSubscriptionDetailsRefresh, token]);

    useEffect(() => {
        void refreshSubscriptionDetails();
    }, [refreshSubscriptionDetails]);

    usePageActivationRefresh(!!token, refreshSubscriptionDetails);

    const hasCurrentAuthState = subscriptionAuthKey === token;
    const currentSubscription = hasCurrentAuthState ? subscription : null;
    const currentError = hasCurrentAuthState ? error : null;
    const currentIsLoading = !hasCurrentAuthState || isLoading;
    const isSubscriptionActive = subscriptionGrantsAccess(currentSubscription);
    const hasManagedSubscription = isSubscriptionNonTerminal(currentSubscription);
    const normalizedStatus = currentSubscription?.PlanStatus?.toLowerCase();
    const isPastDue = normalizedStatus === 'past_due';
    const canResumeCheckout = isSubscriptionCheckoutRecoverable(currentSubscription);
    const isSubscriptionSetToCancel = currentSubscription?.CancelAtPeriodEnd === true
        || (!!currentSubscription?.PlanCancelAt && new Date(currentSubscription.PlanCancelAt) > new Date());
    const periodEndDate = currentSubscription?.PlanCurrentPeriodEnd
        ? new Date(currentSubscription.PlanCurrentPeriodEnd)
        : null;
    const periodEndLabel = periodEndDate && !Number.isNaN(periodEndDate.getTime())
        ? periodEndDate.toLocaleDateString()
        : 'Unavailable';

    const statusLabel = canResumeCheckout
        ? 'Payment incomplete'
        : isPastDue
            ? 'Payment failed'
            : isSubscriptionActive
                ? (isSubscriptionSetToCancel ? 'Canceling' : 'Active')
                : hasManagedSubscription ? 'Needs attention' : 'Inactive';

    return (
        <main className={styles.page}>
            <section className={styles.hero}>
                <div className={styles.heroContent}>
                    <span className={styles.heroTag}>Account</span>
                    <h1 className={styles.heroTitle}>Billing</h1>
                    <p className={styles.heroSubtitle}>
                        Review your subscription and manage payment or cancellation through Stripe.
                    </p>
                </div>
            </section>

            <AccountTabs />

            <section className={styles.section}>
                {currentIsLoading ? (
                    <div className={styles.content}>
                        <h2 className={styles.cardTitle}>Loading subscription</h2>
                        <p className={styles.cardSubtitle}>Fetching your latest plan status.</p>
                    </div>
                ) : null}

                {!currentIsLoading && currentError && currentError.status === 404 ? (
                    <div className={styles.content}>
                        <h2 className={styles.cardTitle}>No active subscription</h2>
                        <p className={styles.cardSubtitle}>Choose a plan to unlock premium fights and exclusive access.</p>
                        <div className={styles.actions}>
                            <Link to={Routes.PRICING} className={styles.primaryButton}>
                                View plans
                            </Link>
                        </div>
                        <BillingPortalPanel
                            token={token}
                            isCancellationScheduled={false}
                            hasManagedSubscription={false}
                        />
                    </div>
                ) : null}

                {!currentIsLoading && currentError && currentError.status !== 404 ? (
                    <div className={styles.content}>
                        <h2 className={styles.cardTitle}>We hit a snag</h2>
                        <p className={styles.cardSubtitle}>{currentError.message}</p>
                        <div className={styles.actions}>
                            <button
                                type="button"
                                className={styles.primaryButton}
                                onClick={() => void refreshSubscriptionDetails()}
                            >
                                Try again
                            </button>
                        </div>
                    </div>
                ) : null}

                {!currentIsLoading && !currentError && currentSubscription ? (
                    <div className={styles.content}>
                        <div className={styles.statusLine}>
                            <span className={isSubscriptionSetToCancel || isPastDue || canResumeCheckout ? styles.warningTag : styles.statusTag}>{statusLabel}</span>
                            {isSubscriptionSetToCancel ? (
                                <span>Your subscription is set to end on {periodEndLabel}.</span>
                            ) : null}
                        </div>
                        <h2 className={styles.cardTitle}>Current plan</h2>
                        <div className={styles.detailGrid}>
                            <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>Plan frequency</span>
                                <span>Recurring per {currentSubscription.PlanInterval}</span>
                            </div>
                            <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>
                                    {isSubscriptionActive && !isSubscriptionSetToCancel ? 'Renews on' : 'Current period ends'}
                                </span>
                                <span>{periodEndLabel}</span>
                            </div>
                        </div>
                        {isPastDue ? (
                            <div className={styles.notice}>
                                Your payment needs attention. Update your payment method in Stripe below.
                            </div>
                        ) : null}
                        {canResumeCheckout ? (
                            <div className={styles.notice}>
                                Your previous checkout was not completed.{' '}
                                <Link to={buildJoinUrl()}>Return to checkout and retry payment</Link>. Your existing
                                plan is selected automatically so the same checkout can resume safely.
                            </div>
                        ) : null}
                        {!hasManagedSubscription ? (
                            <>
                                <p className={styles.cardSubtitle}>
                                    Your previous subscription has ended. Choose a current plan to resubscribe.
                                </p>
                                <div className={styles.actions}>
                                    <Link to={Routes.PRICING} className={styles.primaryButton}>
                                        View plans
                                    </Link>
                                </div>
                            </>
                        ) : null}
                        <BillingPortalPanel
                            token={token}
                            isCancellationScheduled={isSubscriptionSetToCancel}
                            hasManagedSubscription={hasManagedSubscription}
                        />
                    </div>
                ) : null}

            </section>
        </main>
    );
};

export default SubscriptionDetails;
